import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, X } from 'lucide-react';
import { Button } from '../ui';

interface EmailConfirmationModalProps {
  isOpen: boolean;
  email: string;
  onConfirm: () => void;
  onEdit: () => void;
  onClose: () => void;
  isLoading?: boolean;
}

export const EmailConfirmationModal: React.FC<EmailConfirmationModalProps> = ({
  isOpen,
  email,
  onConfirm,
  onEdit,
  onClose,
  isLoading = false,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-avi-primary bg-opacity-10 rounded-full flex items-center justify-center">
                  <Mail className="w-8 h-8 text-avi-primary" />
                </div>
              </div>

              {/* Content */}
              <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
                Confirm Your Email
              </h2>
              <p className="text-center text-gray-600 mb-6">
                We'll send a verification link to:
              </p>

              {/* Email Display */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                <p className="text-center text-gray-900 font-medium break-all">
                  {email}
                </p>
              </div>

              <p className="text-sm text-center text-gray-500 mb-6">
                Please make sure this email is correct. You'll need to verify it to
                continue.
              </p>

              {/* Actions */}
              <div className="flex flex-col gap-3">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={onConfirm}
                  disabled={isLoading}
                  loading={isLoading}
                  className="w-full"
                >
                  Yes, Send Verification
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onEdit}
                  disabled={isLoading}
                  className="w-full"
                >
                  Edit Email
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
